import {
  takeEvery,
  put,
  all,
  call,
  takeLatest,
  takeLeading,
  select,
} from 'redux-saga/effects';
import apiCall from '../../helpers/axiosApiCallWrapper';
import types from './actionTypes';
import * as leadsActions from './actions';
import * as monetizationItemsActions from '../monetizationItems/actions';
import * as nextCallActions from '../nextCall/actions';
import { selectAcquisitionOrderByCampaign } from '../../selectors/acquisitionOrder';
import { selectLeadFieldsByName } from '../../selectors/leadField';
import { actualCountry } from '../../selectors/country';
import { coerceLeadValues } from '../../config/leadFieldValue';
import { getFunctions, httpsCallable } from 'firebase/functions';
import {
  getFirestore,
  collection,
  doc,
  query,
  where,
  serverTimestamp,
  Timestamp,
} from 'firebase/firestore';
import rsf from '../../helpers/firebase';
import importLead from '../../helpers/importLeadHelper';
import { MULTI_CAMPAIGN_IMPORT } from '../../config/importLeadTypes';
import { toDateFirebase } from '../../helpers/sharedFunction';
import toastr from 'toastr';

const db = getFirestore(rsf.app);

const leadTransformer = (lead) => {
  const data = lead.data();
  return {
    id: lead.id,
    ...data,
    name: `${data.firstName || ''} ${data.lastName || ''}`,
    ...(data.birthday && {
      birthday: toDateFirebase(lead, data, 'birthday').toDate(),
    }),
    ...(data.createdAt && {
      createdAt: toDateFirebase(lead, data).toDate(),
    }),
    ...(data.updatedAt && {
      updatedAt: toDateFirebase(lead, data, 'updatedAt').toDate(),
    }),
  };
};

export const getDuplicateLeadsProcessingPlan = (leads) =>
  leads.reduce(
    (plan, lead) => {
      const hasPerson =
        lead.personId && lead.personId !== 'new' && lead.personId !== 'error';

      if (hasPerson) {
        plan.duplicableLeads.push(lead);
      } else {
        plan.skippedLogs.push({
          leadId: lead.id,
          response: 'Skipped: lead without person',
        });
      }

      return plan;
    },
    { duplicableLeads: [], skippedLogs: [] },
  );

function* fetchLeadsSaga({ startDate, endDate, filters, withAi }) {
  try {
    const countryId = yield select((state) => state.Dashboard.countryId);
    const leadsRef = rsf.firestore.createCollectionRefWithFilters(
      'leads',
      countryId,
      startDate,
      endDate,
      filters,
    );

    const leadsSnap = yield call(rsf.firestore.getCollection, leadsRef);
    const leads = leadsSnap.docs.map((lead) => leadTransformer(lead));

    yield put(
      leadsActions.fetchLeadsSuccess(
        leads,
        startDate,
        endDate,
        filters,
        withAi,
      ),
    );
  } catch (error) {
    yield put(leadsActions.fetchLeadsFailure(error));
  }
}

const DAY_MS = 24 * 60 * 60 * 1000;

// Only the two dates the columns show. The repo already has two
// conversation transformers with different field lists
// (store/aiConversations and store/aiConversationMonitor): a third narrow one
// here is deliberate, merging them is not this change's job.
const conversationTransformer = (snap) => {
  const data = snap.data();
  return {
    id: snap.id,
    ...data,
    startedAt: data.startedAt
      ? toDateFirebase(snap, data, 'startedAt').toDate()
      : null,
    endedAt: data.endedAt
      ? toDateFirebase(snap, data, 'endedAt').toDate()
      : null,
  };
};

/**
 * The agent's side of the leads just read, as a map leadId -> conversation for
 * the AI columns of the table.
 *
 * One query on the same range as the leads, padded by a day on each side: a
 * lead that came in before midnight is routed after it. It rides the
 * (countryId, startedAt) index the monitor already needs, and it never costs
 * more than the leads fetch it follows, because a conversation belongs to a
 * routed lead.
 *
 * The map is keyed through the leads' own `aiConversationId`, which the
 * functions rewrite whenever they open one: so a lead shows the conversation
 * it points at, or nothing. Nothing is the honest answer for a pointer outside
 * the window — the modal, which reads by leadId, is the one that knows.
 */
export function* fetchLeadsAiConversationsSaga({
  leads,
  startDate,
  endDate,
  withAi,
}) {
  // Only the view with the AI columns asks for this: a publisher session, or
  // the query test table, must never pull every aiConversations document of
  // the country into its browser for columns it never renders.
  if (!withAi) return;

  const country = yield select(actualCountry);
  if (!country || !country.aiModule) return;

  const leadOfConversation = new Map(
    (leads || [])
      .filter(({ aiConversationId }) => !!aiConversationId)
      .map((lead) => [lead.aiConversationId, lead.id]),
  );
  if (!leadOfConversation.size) return;

  yield put(leadsActions.fetchLeadsAiConversations());

  try {
    const countryId = yield select((state) => state.Dashboard.countryId);
    const conversationsRef = query(
      collection(db, 'aiConversations'),
      where('countryId', '==', countryId),
      where(
        'startedAt',
        '>=',
        // startDate/endDate can be a persisted ISO string on the
        // country-switch path (state.Dashboard.startDate survives a reload),
        // not only the Date the leads fetch passes directly.
        Timestamp.fromDate(new Date(new Date(startDate).getTime() - DAY_MS)),
      ),
      where(
        'startedAt',
        '<=',
        Timestamp.fromDate(new Date(new Date(endDate).getTime() + DAY_MS)),
      ),
    );

    const snap = yield call(rsf.firestore.getCollection, conversationsRef);

    const aiByLead = {};
    snap.docs.forEach((doc) => {
      const leadId = leadOfConversation.get(doc.id);
      if (leadId) aiByLead[leadId] = conversationTransformer(doc);
    });

    // A country switch while this read is in flight must not let the
    // previous country's map land in the slice the switch just reset.
    const stillTheSameCountry = yield select(
      (state) => state.Dashboard.countryId,
    );
    if (stillTheSameCountry !== countryId) return;

    yield put(leadsActions.fetchLeadsAiConversationsSuccess(aiByLead));
  } catch (error) {
    yield put(leadsActions.fetchLeadsAiConversationsFailure(error));
  }
}

function* fetchSingleLeadSaga({ leadId }) {
  try {
    const leadRef = doc(db, 'leads', leadId);
    const leadDoc = yield call(rsf.firestore.getDocument, leadRef);
    const lead = leadDoc.exists ? leadTransformer(leadDoc) : null;

    yield put(leadsActions.fetchSingleLeadSuccess(lead));
  } catch (error) {
    yield put(leadsActions.fetchSingleLeadFailure(error));
  }
}

export function* createLeadSaga({ lead }) {
  try {
    const countryId = yield select((state) => state.Dashboard.countryId);
    const leadsRef = collection(db, 'leads');

    const acquisitionOrder = yield select(
      selectAcquisitionOrderByCampaign(lead.campaignId),
    );

    const { closure, sendInitialSurvey } = lead;

    delete lead.create;
    delete lead.closure;
    delete lead.sendInitialSurvey;

    const leadFieldsByName = yield select(selectLeadFieldsByName);

    const newLead = yield call(rsf.firestore.addDocument, leadsRef, {
      ...lead,
      /**
       * The date fields used to be written as the strings the form holds,
       * and their readers call `.toDate()`. Every field whose leadField has
       * typeField 'date' is now a Timestamp at UTC midnight.
       *
       * deleteInvalid: false because addDocument rejects deletions. An
       * unreadable date stays as typed, and normalizeLead decides its fate a
       * moment later.
       */
      ...coerceLeadValues(lead, leadFieldsByName, { deleteInvalid: false }),
      countryId,
      acquisitionOrderId: acquisitionOrder.id,
      sector: acquisitionOrder.sector || 'finance',
      source: 'CRM',
      qualified: closure === 'no' ? 'new' : 'completed',
      survey: sendInitialSurvey === 'default' ? 'new' : 'notNecessary',
    });

    if (closure !== 'no') yield call(createMonetizationItems, lead, newLead.id);

    yield put(leadsActions.createLeadSuccess());
    toastr.success('Lead created!', '');
  } catch (error) {
    yield put(leadsActions.createLeadFailure(error));
  }
}

export function* updateLeadSaga({ lead }) {
  try {
    const leadId = lead.id;
    const leadsRef = doc(db, 'leads', lead.id);
    const acquisitionOrder = yield select(
      selectAcquisitionOrderByCampaign(lead.campaignId),
    );

    delete lead.id;
    delete lead.name;
    delete lead.create;

    const leadFieldsByName = yield select(selectLeadFieldsByName);

    yield call(
      rsf.firestore.setDocument,
      leadsRef,
      {
        ...lead,
        ...(acquisitionOrder && {
          acquisitionOrderId: acquisitionOrder.id,
          sector: acquisitionOrder.sector,
        }),
        /**
         * Was a conversion written on the name `birthday` alone, anchored to
         * the local midnight of whoever typed it; any other date field stayed
         * a string. An emptied or unreadable date is deleted.
         */
        ...coerceLeadValues(lead, leadFieldsByName),
        /**
         * createdAt has typeField 'date' but is an instant: anchoring it to
         * midnight would throw away the time the lead came in. Its own line
         * stays, and sits after the conversion so that it wins.
         */
        ...(lead.createdAt && {
          createdAt: Timestamp.fromDate(new Date(lead.createdAt)),
        }),
        updatedAt: serverTimestamp(),
      },
      { merge: true },
    );

    if (
      Array.isArray(lead.monetizationOrders) &&
      lead.monetizationOrders.length > 0
    )
      yield call(createMonetizationItems, lead, leadId);

    yield put(leadsActions.fetchSingleLead(leadId));
    yield put(nextCallActions.setDirtyLeadForm(false));
    yield put(leadsActions.updateLeadSuccess());
    toastr.success('Lead updated!', '');
  } catch (error) {
    yield put(leadsActions.updateLeadFailure(error));
  }
}

function* createMonetizationItems(lead, leadId) {
  return Array.isArray(lead.monetizationOrders)
    ? yield all(
        //Positive closure
        lead.monetizationOrders.map((monetizationOrderId) =>
          put(
            monetizationItemsActions.createMonetizationItem({
              campaignId: lead.campaignId,
              leadId,
              monetizationOrderId,
            }),
          ),
        ),
      )
    : yield put(
        //Negative closure
        monetizationItemsActions.createMonetizationItem({
          campaignId: lead.campaignId,
          negativeOutcome: lead.negativeOutcome,
          leadId,
        }),
      );
}

function* importLeadsSaga({
  importLeadType,
  campaignId,
  startDate,
  endDate,
  costPerLead,
  leads,
}) {
  try {
    const leadsFromCSV = leads.map((lead) => lead.data);
    const importTime = new Date().getTime();
    let leadsToImport = [];

    //Validate CSV
    leadsFromCSV.forEach((lead, index) => {
      if (campaignId === MULTI_CAMPAIGN_IMPORT.value && !lead.campaignId)
        throw new Error(
          `Check CSV at row ${
            index + 2
          }: campaignId is mandatory in 'Multi' import. Please retry!`,
        );

      const leadData = importLead(importLeadType, lead);
      const { error } = leadData;
      if (error)
        throw new Error(
          `Check CSV at row ${index + 2}: ${error}. Please retry!`,
        );
      else leadsToImport.push(leadData);
    });

    const countryId = yield select((state) => state.Dashboard.countryId);
    const rangeLeadsRef = query(
      collection(db, 'leads'),
      where('createdAt', '>=', startDate),
      where('createdAt', '<=', endDate),
      where('countryId', '==', countryId),
    );

    const rangeLeadsSnap = yield call(
      rsf.firestore.getCollection,
      rangeLeadsRef,
    );

    let importLeads = [];

    if (!rangeLeadsSnap.empty) {
      leadsToImport.forEach((leadToImport) => {
        let existingLead = false;
        rangeLeadsSnap.forEach((lead) => {
          const data = lead.data();
          if (data.phone === leadToImport.phone) {
            existingLead = true;
            return;
          }
        });
        !existingLead && importLeads.push(leadToImport);
      });
    } else importLeads = [...leadsToImport];

    const leadsRef = collection(db, 'leads');
    yield all(
      importLeads.map((lead) =>
        call(rsf.firestore.addDocument, leadsRef, {
          ...lead,
          importFrom: importLeadType,
          importTime,
          campaignId: lead.campaignId || campaignId,
          countryId,
          source: lead.source || 'CRM',
          ...(costPerLead > 0 && { cost: costPerLead }),
        }),
      ),
    );

    yield put(leadsActions.importLeadsSuccess());
    toastr.success(`${importLeads.length} Leads imported!`, '');
  } catch (error) {
    yield put(leadsActions.importLeadsFailure(error));
    toastr.error(error.message, 'Error');
  }
}

function* duplicateLeadsSaga({ campaignId, leads }) {
  try {
    if (!Array.isArray(leads)) throw new Error('Leads must be an array!');

    const { duplicableLeads, skippedLogs } =
      getDuplicateLeadsProcessingPlan(leads);

    let responses = yield all(
      duplicableLeads.map((lead) => {
        const { personId } = lead;
        return call(
          apiCall,
          'GET',
          `/createLeadFromPerson/${personId}/${campaignId}?responseType=text&isDuplicated=1`,
        );
      }),
    );
    responses = responses.map((response, index) => ({
      leadId: duplicableLeads[index].id,
      response,
    }));
    yield put(
      leadsActions.duplicateLeadsSuccess([...responses, ...skippedLogs]),
    );
  } catch (error) {
    yield put(leadsActions.duplicateLeadsFailure(error));
    toastr.error(error, 'Error');
  }
}

function* closeExcessLeadsSaga({ leads, negativeOutcome }) {
  try {
    if (!Array.isArray(leads)) throw new Error('Leads must be an array!');
    if (!negativeOutcome) throw new Error('negativeOutcome is required!');

    const functions = getFunctions();
    const closeLeadQualificationFunction = httpsCallable(
      functions,
      'closeLeadQualification-closeLeadQualification',
    );

    let responses = [];

    for (let i = 0; i < leads.length; i++) {
      const lead = leads[i];
      try {
        const { data } = yield call(closeLeadQualificationFunction, {
          leadId: lead.id,
          negativeOutcome,
        });

        responses.push({
          leadId: lead.id,
          response: data?.message || data?.error?.message,
          status: data?.status,
        });
      } catch (error) {
        responses.push({
          leadId: lead.id,
          response: error.message,
          status: 500,
        });
      }
    }

    yield put(leadsActions.closeExcessLeadsSuccess(responses));
  } catch (error) {
    yield put(leadsActions.closeExcessLeadsFailure(error));
    toastr.error(error.message, 'Error');
  }
}

function* addPushTokenLeadSaga({ leadId, pushToken, os, browser }) {
  try {
    if (!leadId) throw new Error('LeadId is mandatory!');
    if (!pushToken) throw new Error('PushToken is mandatory!');

    yield call(apiCall, 'POST', `/addPushTokenLead/${leadId}`, {
      token: pushToken,
      pushType: 'web',
      os,
      browser,
    });
    yield put(leadsActions.addPushTokenLeadSuccess());
  } catch (error) {
    yield put(leadsActions.addPushTokenLeadFailure(error));
    toastr.error(error, 'Error');
  }
}

function* allLeadsSaga() {
  yield all([
    takeLatest(types.FETCH_LEADS.REQUEST, fetchLeadsSaga),
    // takeLatest: a second refresh cancels the first read, the way the
    // monitor does with the lead names. The reset is in the pattern so that
    // it cancels a read still in flight — changeCountrySaga dispatches it,
    // and without it a pending read could write the previous country's map,
    // or its failure, into the slice that was just emptied. The reset action
    // carries no `withAi`, so the worker it starts exits at once.
    takeLatest(
      [types.FETCH_LEADS.SUCCESS, types.RESET_LEAD_STATE],
      fetchLeadsAiConversationsSaga,
    ),
    takeEvery(types.IMPORT_LEADS.REQUEST, importLeadsSaga),
    takeEvery(types.FETCH_SINGLE_LEAD.REQUEST, fetchSingleLeadSaga),
    takeLeading(types.CREATE_LEAD.REQUEST, createLeadSaga),
    takeLeading(types.UPDATE_LEAD.REQUEST, updateLeadSaga),
    takeLeading(types.DUPLICATE_LEADS.REQUEST, duplicateLeadsSaga),
    takeLeading(types.CLOSE_EXCESS_LEADS.REQUEST, closeExcessLeadsSaga),
    takeLeading(types.ADD_PUSH_TOKEN_LEAD.REQUEST, addPushTokenLeadSaga),
  ]);
}

export default allLeadsSaga;
